import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  House, Envelope, Bell, Lightning, ChartBar, Gear, SignOut, List, X, Lock
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: House, id: "nav-dashboard" },
  { to: "/inbox", label: "Inbox", icon: Envelope, id: "nav-inbox", capability: "bulk_delete" },
  { to: "/subscriptions", label: "Subscriptions", icon: Bell, id: "nav-subs", capability: "subscription_management" },
  { to: "/rules", label: "Auto-rules", icon: Lightning, id: "nav-rules", capability: "auto_rules" },
  { to: "/analytics", label: "Analytics", icon: ChartBar, id: "nav-analytics", capability: "analytics" },
  { to: "/settings", label: "Settings", icon: Gear, id: "nav-settings" },
];

function SidebarBody({ user, onLogout, onClose, access }) {
  return (
    <>
      <div className="px-6 py-5 border-b border-border flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link" onClick={onClose}>
          <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
          <span className="font-display text-lg font-bold tracking-tight">Easy Mail</span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="md:hidden" aria-label="Close menu" data-testid="sidebar-close">
            <X size={22} />
          </button>
        )}
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {nav.map((n) => {
          const locked = n.capability && access && !access.capabilities?.[n.capability];
          const target = locked ? "/pricing" : n.to;
          return (
            <NavLink
              key={n.to}
              to={target}
              data-testid={n.id}
              onClick={onClose}
              title={locked ? "Locked: upgrade required" : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 ${
                  isActive ? "border-primary bg-secondary font-medium" : "border-transparent hover:bg-secondary/60 text-foreground/80"
                } ${locked ? "opacity-70" : ""}`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span className="flex items-center gap-2">
                {n.label}
                {locked && <Lock size={14} />}
              </span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="text-xs text-muted-foreground font-mono mb-2 uppercase tracking-wider">Signed in</div>
        <div className="text-sm font-medium truncate" data-testid="user-email">{user?.email}</div>
        <div className="text-xs text-muted-foreground mb-3 capitalize">Plan: {user?.plan || "—"}</div>
        <Button variant="outline" size="sm" className="w-full rounded-none gap-2" onClick={onLogout} data-testid="logout-btn">
          <SignOut size={16} /> Logout
        </Button>
      </div>
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState(null);

  useEffect(() => {
    api.get("/payments/access").then((r) => setAccess(r.data)).catch(() => setAccess({ capabilities: {} }));
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 border-r border-border flex-col shrink-0">
        <SidebarBody user={user} onLogout={doLogout} access={access} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="fixed inset-0 bg-foreground/40 z-40 md:hidden" onClick={() => setOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-background border-r border-border flex flex-col z-50 md:hidden" data-testid="mobile-sidebar">
            <SidebarBody user={user} onLogout={doLogout} onClose={() => setOpen(false)} access={access} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-background sticky top-0 z-30">
          <button onClick={() => setOpen(true)} className="p-1" aria-label="Open menu" data-testid="sidebar-open">
            <List size={24} />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
            <span className="font-display font-bold">Easy Mail</span>
          </Link>
          <div className="w-7" />
        </div>
        <div className="flex-1">{children}</div>
        <footer data-testid="app-footer-legal" className="border-t border-border py-4 px-6 text-center text-xs text-muted-foreground bg-background">
          <Link to="/legal" className="hover:text-primary underline mx-2" data-testid="footer-link-legal">Legal Documents and Policies</Link>
          <span className="mx-3 font-mono opacity-60">© 2026 Easy Mail LLC</span>
        </footer>
      </main>
    </div>
  );
}
